import { useState, useRef, useCallback } from 'react';
import { Upload } from 'lucide-react';
import { useProjectStore } from '../../stores/useProjectStore';
import type { ProjectFile } from '../../types/project';
import styles from './Modal.module.css';

interface ImportProjectModalProps {
  onClose: () => void;
  onCreated: (projectId: string) => Promise<void>;
}

function detectLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    ts: 'typescript', tsx: 'typescriptreact', js: 'javascript', jsx: 'javascriptreact',
    rs: 'rust', toml: 'toml', json: 'json', md: 'markdown', css: 'css',
    html: 'html', yaml: 'yaml', yml: 'yaml', sh: 'shell', mist: 'mist',
    sol: 'solidity', txt: 'plaintext',
  };
  return map[ext] ?? 'plaintext';
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB per file
const MAX_FILES = 500;

export function ImportProjectModal({ onClose, onCreated }: ImportProjectModalProps) {
  const [name, setName] = useState('');
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createProject = useProjectStore(s => s.createProject);

  const processFiles = useCallback(async (fileList: FileList | File[]) => {
    setError(null);
    const arr = Array.from(fileList);

    if (arr.length > MAX_FILES) {
      setError(`Too many files. Maximum is ${MAX_FILES}.`);
      return;
    }

    const result: ProjectFile[] = [];

    for (const f of arr) {
      if (f.size > MAX_FILE_SIZE) {
        setError(`File "${f.name}" exceeds 2MB limit. Skipped.`);
        continue;
      }

      try {
        const content = await f.text();
        const relativePath = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
        result.push({
          id: crypto.randomUUID(),
          name: f.name,
          path: relativePath,
          content,
          language: detectLanguage(f.name),
          isModified: false,
        });
      } catch {
        setError(`Failed to read file "${f.name}".`);
      }
    }

    setFiles(result);
    if (result.length > 0 && !name) {
      const firstPath = result[0].path;
      const folderName = firstPath.includes('/') ? firstPath.split('/')[0] : 'imported-project';
      setName(folderName);
    }
  }, [name]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  const handleImport = async () => {
    const projectName = name.trim();
    if (!projectName) {
      setError('Project name is required.');
      return;
    }
    if (files.length === 0) {
      setError('No files to import.');
      return;
    }
    setIsImporting(true);
    setError(null);

    try {
      const id = await createProject(projectName, 'imported', files);
      await onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to import project.');
      setIsImporting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Import from Computer</h2>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div className={styles.modalBody}>
          {error && <div className={styles.errorBanner}>{error}</div>}

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Project Name</label>
            <input
              className={styles.fieldInput}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="imported-project"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Files</label>
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={24} />
              <span>Drag files here or click to browse</span>
              <span style={{ fontSize: '11px' }}>Max 2MB per file, {MAX_FILES} files total</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.hiddenInput}
              multiple
              onChange={e => { if (e.target.files) processFiles(e.target.files); }}
            />
          </div>

          {files.length > 0 && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>{files.length} file(s) selected</label>
              <div className={styles.fileList}>
                {files.map(f => (
                  <div key={f.id} className={styles.fileListItem}>{f.path}</div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button className={styles.submitBtn} onClick={handleImport} disabled={isImporting || files.length === 0}>
            {isImporting ? 'Importing...' : 'Import Project'}
          </button>
        </div>
      </div>
    </div>
  );
}
